import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { EstateSubcontractorData, SubcontractorData } from 'src/lib/fliko/models/estate/subcontractor.data';
import { CreateServiceUser, UserCreationState } from 'src/lib/fliko/models/users/createdUser.data';
import { ServiceUserData } from 'src/lib/fliko/models/users/user.data';
import { Update, UpdateIdOnly } from 'src/lib/fliko/types/update.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { ContextService } from 'src/app/view/user-context/services/context.service';

@Injectable({ providedIn: 'root' })
export class SubcontractorsService {
  constructor(
    private dbSrv: DatabaseService,
    private ctx: ContextService
  ) {}

  // #region Subcontractor
  /**
   * @path /subcontractor/
   * @key id
   */
  private get subcontractor() {
    return this.dbSrv.commonFactory.getCollection<SubcontractorData>('subcontractor');
  }

  public getSubcontractors$(): Observable<SubcontractorData[]> {
    return this.subcontractor.getList();
  }

  public getSubcontractorsInEstate$(): Observable<SubcontractorData[]> {
    return combineLatest([this.getSubcontractors$(), this.ctx.getEstateId$()]).pipe(
      map(([data, eId]) => (eId ? data.filter((sub) => sub.estates?.includes(eId)) : []))
    );
  }

  public getSubcontractor$(id: string): Observable<SubcontractorData | undefined> {
    return this.getSubcontractors$().pipe(map((subcontractors) => subcontractors.find((sub) => sub.id === id)));
  }

  public setSubcontractor(data: SubcontractorData): Promise<void> {
    return this.subcontractor.setElement(data.id, data);
  }

  public setSubcontractorEstates(subcontractorId: string, estateIds: string[]): Promise<void> {
    const filtered = new Set(estateIds.filter((x) => !!x));
    return this.subcontractor.updateElement(subcontractorId, { estates: [...filtered] });
  }

  public updateSubcontractor(data: UpdateIdOnly<SubcontractorData>): Promise<void> {
    return this.subcontractor.updateElement(data.id, data);
  }
  // #endregion

  // #region Estate subcontractor
  /**
   * @path /estateSubcontractor/$estateId/
   * @key subcontractorId
   */
  private get estateSubcontractor() {
    return this.dbSrv.estateFactory.getCollection<EstateSubcontractorData>('estateSubcontractor/$eId');
  }

  public getEstateSubcontractors$(): Observable<EstateSubcontractorData[]> {
    return this.estateSubcontractor.getList();
  }

  public getEstateSubcontractor$(subcontractorId: string): Observable<EstateSubcontractorData | Nullish> {
    return this.estateSubcontractor.getElement(subcontractorId);
  }

  public updateEstateSubcontractor(data: Update<EstateSubcontractorData>): Promise<void> {
    return this.estateSubcontractor.updateElement(data.id, data);
  }
  // #endregion

  // #region Service user
  /**
   * @path /service/user/
   * @key userId
   */
  private get serviceUser() {
    return this.dbSrv.commonFactory.getCollectionReadOnly<ServiceUserData>('service/user');
  }

  public getServiceUsers$(): Observable<ServiceUserData[]> {
    return this.serviceUser.getList();
  }

  /**
   * @path /service/createUser/
   * @key id
   */
  private get createServiceUser() {
    return this.dbSrv.commonFactory.getCollection<CreateServiceUser>('service/createUser');
  }

  public getCreateServiceUsers$(estateId: string): Observable<CreateServiceUser[]> {
    return this.createServiceUser.getList().pipe(map((data) => data.filter((csu) => csu.estateIds?.includes(estateId))));
  }

  public setCreateServiceUser(data: CreateServiceUser): Promise<void> {
    return this.createServiceUser.setElement(data.id, data);
  }

  public resendCreateServiceUser(id: string): Promise<void> {
    return this.createServiceUser.updateElement(id, { sendDate: Date.now() });
  }

  public cancelCreateServiceUser(id: string): Promise<void> {
    return this.createServiceUser.updateElement(id, { status: UserCreationState.DELETED });
  }

  // #endregion
}
